import React, { PropsWithChildren, createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  TrackerWithRelatedContent,
  useGetTrackersWithRelatedContentFunctionRequest,
} from "shared/request/myHealthyAdvantageApi";
import { ClientInfoState } from "shared/core/state/clientInfoState";

export type ContentContextType = {
  getFourWeekPlanIds: (trackerType: string) => string[];
  getLearningMaterialCategoryIds: (trackerType: string) => string[];
  relatedContentLoading: boolean;
  relatedContentError: boolean;
};

export const ContentContext = createContext<ContentContextType>({} as ContentContextType);
export const useContentContext = () => useContext(ContentContext);

type TrackerMap = Record<string, TrackerWithRelatedContent | null>;

const DEFAULT_TRACKERS = {
  sleep: null,
  steps: null,
  activity: null,
  water: null,
  breathingexercises: null,
} as const;

const mapResponseDataToTrackerWithRelatedContent = (
  data: TrackerWithRelatedContent[],
  currentState: TrackerMap
): TrackerMap => {
  const next: TrackerMap = { ...currentState };
  for (const item of data) {
    if (!item || !item.identifier) continue;
    next[item.identifier] = item;
  }
  return next;
};

export const ContentContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [trackersWithRelatedContent, setTrackersWithRelatedContent] = useState<TrackerMap>(
    () => ({ ...DEFAULT_TRACKERS })
  );

  const { data, isLoading, error } = useGetTrackersWithRelatedContentFunctionRequest({
    xClientCode: ClientInfoState.getCode(),
  });

  useEffect(() => {
    if (!data || data.length === 0) return;
    setTrackersWithRelatedContent((prev) => mapResponseDataToTrackerWithRelatedContent(data, prev));
  }, [data]);

  const getFourWeekPlanIds = useCallback(
    (trackerType: string) => {
      const tracker = trackersWithRelatedContent[trackerType];
      return tracker?.fourWeekPlans?.map((p) => p.id) ?? [];
    },
    [trackersWithRelatedContent]
  );

  const getLearningMaterialCategoryIds = useCallback(
    (trackerType: string) => {
      const tracker = trackersWithRelatedContent[trackerType];
      return tracker?.learningMaterialCategories?.map((c) => c.id) ?? [];
    },
    [trackersWithRelatedContent]
  );

  const contextValue = useMemo<ContentContextType>(
    () => ({
      getFourWeekPlanIds,
      getLearningMaterialCategoryIds,
      relatedContentLoading: isLoading,
      relatedContentError: !!error,
    }),
    [getFourWeekPlanIds, getLearningMaterialCategoryIds, isLoading, error]
  );

  return <ContentContext.Provider value={contextValue}>{children}</ContentContext.Provider>;
};
